/*
  This file exists to handle edge-case scenarios whereby the primary header
  UI elements on mobile don't fit within the viewport width. For example, if
  there's a wide logo and verbose featured navigation link. In this scenario
  the featured navigation element is moved inside the mobile menu.
*/

import { CSS } from "../../constants/css";
import { debounce } from "../../utils/debounce";

interface DomElements {
  $primaryHeader: HTMLElement | null;
  $nav: HTMLElement | null;
  $featured: HTMLElement | null;
}

const debounceRate = 250;

const checkLayout = (dom: DomElements) => {
  if (!dom.$primaryHeader || !dom.$nav || !dom.$featured) {
    return;
  }

  // 'Remember' current overflow value
  const currentOverflow = dom.$primaryHeader.style.overflow;

  // Move featured el back to original DOM position and reset to default UI state
  dom.$primaryHeader.append(dom.$featured);
  dom.$primaryHeader.style.overflow = "hidden";
  dom.$featured.classList.remove(CSS.hidden);

  // Check for overflow
  const isOverflowing = dom.$primaryHeader.clientWidth < dom.$primaryHeader.scrollWidth;

  // Revert UI state of primaryHeader
  dom.$primaryHeader.style.overflow = currentOverflow;

  // If overflowing, move featured element into mobile menu and add 'cloned' class.
  if (isOverflowing) {
    dom.$featured.classList.add(CSS.clone);
    dom.$nav.appendChild(dom.$featured);
    return;
  }

  dom.$featured.classList.remove(CSS.clone);
};

const init = () => {
  const $primaryHeaders = document.querySelectorAll<HTMLElement>(".js-featured-cta-layout");
  $primaryHeaders.forEach(($el) => {
    const dom: DomElements = {
      $primaryHeader: $el,
      $nav: $el.querySelector<HTMLElement>(".js-featured-cta-layout-nav"),
      $featured: $el.querySelector<HTMLElement>(`.js-featured-cta-layout-featured:not(${CSS.clone})`),
    };
    checkLayout(dom);
  });
};

export default () => {
  init();
  const debouncedCheck = debounce(init, debounceRate);
  window.addEventListener("resize", debouncedCheck);
  window.addEventListener("orientationchange", debouncedCheck);
};
